import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { PageHeader } from '@/components/common/PageHeader'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Project = Tables<'projects'>

interface LineItem {
  productId: string
  productName: string
  sku: string
  configuration: Record<string, unknown>
  quantity: number
  unitPrice: number
  lineTotal: number
  description: string
}

export function QuoteCreatePage() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [projects, setProjects] = useState<Project[]>([])
  const [selectedProjectId, setSelectedProjectId] = useState(searchParams.get('project') || '')
  const [lineItems, setLineItems] = useState<LineItem[]>([])
  const [discountPct, setDiscountPct] = useState(0)
  const [internalNotes, setInternalNotes] = useState('')
  const [customerNotes, setCustomerNotes] = useState('')
  const [validUntil, setValidUntil] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!profile?.organization_id) return
    supabase.from('projects').select('*').eq('organization_id', profile.organization_id).in('status', ['draft', 'active']).order('name')
      .then(({ data }) => setProjects(data || []))
  }, [profile])

  // Pre-populate line item from configurator
  useEffect(() => {
    const configStr = searchParams.get('config')
    if (configStr) {
      try {
        const config = JSON.parse(configStr)
        setLineItems([{
          productId: config.productId,
          productName: config.productName,
          sku: config.sku,
          configuration: config,
          quantity: config.quantity || 1,
          unitPrice: config.unitPrice,
          lineTotal: config.lineTotal,
          description: `${config.productName} - ${config.dimensions?.width}x${config.dimensions?.height}mm`,
        }])
      } catch { /* ignore parse errors */ }
    }
  }, [searchParams])

  const subtotal = lineItems.reduce((sum, item) => sum + item.lineTotal, 0)
  const discountAmount = subtotal * (discountPct / 100)
  const total = subtotal - discountAmount

  const handleSubmit = async () => {
    if (!profile?.organization_id || !selectedProjectId) return
    setLoading(true)
    setError('')

    const refCode = 'QTE-' + new Date().getFullYear() + '-' + String(Date.now()).slice(-4)

    const { data: quote, error: err } = await supabase.from('quotes').insert({
      project_id: selectedProjectId,
      organization_id: profile.organization_id,
      created_by: profile.id,
      reference_code: refCode,
      status: 'draft',
      subtotal,
      discount_pct: discountPct,
      discount_amount: discountAmount,
      total,
      internal_notes: internalNotes || null,
      customer_notes: customerNotes || null,
      valid_until: validUntil || null,
    }).select().single()

    if (err || !quote) {
      setError(err?.message || 'Failed to create quote')
      setLoading(false)
      return
    }

    // Insert line items
    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i]
      await supabase.from('quote_line_items').insert({
        quote_id: quote.id,
        product_id: item.productId,
        line_number: i + 1,
        quantity: item.quantity,
        description: item.description,
        configuration: item.configuration as import('@/lib/database.types').Json,
        unit_price: item.unitPrice,
        line_total: item.lineTotal,
      })
    }

    await supabase.from('activity_log').insert({
      organization_id: profile.organization_id,
      project_id: selectedProjectId,
      actor_id: profile.id,
      activity_type: 'quote_created',
      entity_type: 'quote',
      entity_id: quote.id,
      summary: `Quote ${refCode} created (£${total.toLocaleString()})`,
    })

    navigate(`/gaviota/quotes/${quote.id}`)
  }

  return (
    <div>
      <PageHeader title="New Quote" description="Create a quotation — optimised for speed" />

      <div style={{ maxWidth: 800 }}>
        {error && <div style={{ background: 'var(--color-error-muted)', color: 'var(--color-error)', padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>{error}</div>}

        <div className={css.card} style={{ marginBottom: 'var(--space-4)' }}>
          <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>Project</h3>
          <select className={css.formSelect} value={selectedProjectId} onChange={e => setSelectedProjectId(e.target.value)}>
            <option value="">Select a project...</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.reference_code} — {p.name}</option>)}
          </select>
        </div>

        <div className={css.card} style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
            <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)' }}>Line Items</h3>
            <Link to="/gaviota/catalog" className={css.btnSecondary} style={{ fontSize: 'var(--text-xs)' }}>Add from Catalog</Link>
          </div>

          {lineItems.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: 'var(--space-8)' }}>
              No items yet. <Link to="/gaviota/catalog">Browse the catalog</Link> to configure and add products.
            </p>
          ) : (
            <div>
              {lineItems.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-3) 0', borderBottom: '1px solid var(--color-border-subtle)' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>{item.productName}</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)' }}>{item.sku} · Qty: {item.quantity}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>£{item.lineTotal.toLocaleString()}</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>£{item.unitPrice.toLocaleString()} each</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={css.card} style={{ marginBottom: 'var(--space-4)' }}>
          <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>Pricing</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)' }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Subtotal</span><span>£{subtotal.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--text-sm)' }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Discount</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                <input type="number" className={css.formInput} style={{ width: 80, padding: 'var(--space-1) var(--space-2)', textAlign: 'right' }} value={discountPct} onChange={e => setDiscountPct(Number(e.target.value))} min={0} max={100} />
                <span>%</span>
                {discountAmount > 0 && <span style={{ color: 'var(--color-text-muted)' }}>(−£{discountAmount.toLocaleString()})</span>}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
              <span style={{ fontSize: 'var(--text-lg)', fontWeight: 600 }}>Total</span>
              <span style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--color-primary-dark)' }}>£{total.toLocaleString()}</span>
            </div>
          </div>
        </div>

        <div className={css.card} style={{ marginBottom: 'var(--space-6)' }}>
          <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>Details</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Valid Until</label>
              <input type="date" className={css.formInput} value={validUntil} onChange={e => setValidUntil(e.target.value)} />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Customer Notes (visible on quote)</label>
              <textarea className={css.formTextarea} value={customerNotes} onChange={e => setCustomerNotes(e.target.value)} placeholder="Notes for the customer..." />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Internal Notes</label>
              <textarea className={css.formTextarea} value={internalNotes} onChange={e => setInternalNotes(e.target.value)} placeholder="Internal team notes..." />
            </div>
          </div>
        </div>

        <button className={css.btnPrimary} onClick={handleSubmit} disabled={loading || !selectedProjectId || lineItems.length === 0} style={{ width: '100%', justifyContent: 'center', padding: 'var(--space-4)' }}>
          {loading ? 'Creating Quote...' : 'Create Quote'}
        </button>
      </div>
    </div>
  )
}
